import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/auth-card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Link } from "@/components/ui/link";
import { Text } from "@/components/ui/text";
import { useAppForm } from "@/hooks/useCreateForm";
import { authClient } from "@/lib/authClient";

const loginForm = type({
  email: "string.email",
  password: "string >= 8",
});

// Todo add params for details about redirects and the like

export const Route = createFileRoute("/_unauthed/login/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const params = Route.useSearch();
  const { mutate, isPending } = useMutation({
    mutationFn: (data: typeof loginForm.infer) => authClient.signIn.email(data),
    onSuccess: async (response, data) => {
      if (response.error) {
        toast.error(response.error.message);
        authClient.sendVerificationEmail({
          email: data.email,
        });
        // @ts-expect-error
      } else if (response.data?.twoFactorRedirect !== true) {
        await queryClient.refetchQueries(trpc.auth.session.queryOptions());

        navigate({
          to: params.to ?? "/dashboard",
        });
      }
    },
  });
  if (params.message) {
    toast.info(params.message);
    navigate({
      search: {
        message: undefined,
      },
    });
  }

  const form = useAppForm({
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      mutate(value);
    },
    validators: {
      onSubmit: loginForm,
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <AuthCard
        description={
          <Text as="span" variant="muted">
            Don't have an account?{" "}
            <Link to="/sign-up" variant="default">
              Sign up for free
            </Link>
          </Text>
        }
        title="Sign in to your account"
      >
        <Form>
          <form.AppField
            children={(field) => {
              return (
                <field.TextField
                  label="Email Address"
                  placeholder=""
                  required={false}
                />
              );
            }}
            name="email"
          />

          <form.AppField
            children={(field) => (
              <field.PasswordField
                label="Password"
                placeholder=""
                required={false}
                showForgotPassword
              />
            )}
            name="password"
          />
          <Button
            className="mt-2"
            disabled={isPending}
            type="submit"
            variant="auth-primary"
          >
            {isPending && <Loader2 className="mr-2 animate-spin" />}
            Sign in
          </Button>
        </Form>
      </AuthCard>
    </form>
  );
}
